import { Button, CircularProgress, Typography } from "@suraasa/placebo-ui"
import api from "api"
import { SocialAuthProvider } from "api/resources/auth/types"
import { APIError } from "api/utils"
import PageContainer from "components/PageContainer"
import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"

import { getCleverRedirectUri } from "../utils"

// Read-only and safe to call more than once (e.g. React 18 StrictMode's
// dev-only double-invocation of lazy useState initializers) — actually
// clearing the stored state happens separately, in the effect below.
const getCallbackValidationError = (searchParams: URLSearchParams) => {
  if (!searchParams.get("code")) {
    return "Missing authorization code."
  }

  const returnedState = searchParams.get("state")
  const cleverError = searchParams.get("error")
  const expectedState = sessionStorage.getItem(
    BROWSER_STORAGE_KEYS.cleverOAuthState
  )

  if (cleverError) {
    return "Clever sign-in was cancelled or failed."
  }

  if (!returnedState || !expectedState || returnedState !== expectedState) {
    return "Something went wrong. Please try linking your account again."
  }

  return null
}

/**
 * Terminus of the Clever OAuth2 redirect kicked off from Settings >
 * Social Accounts. Unlike a sign-in flow, the user is already authenticated
 * here (we never left this origin), so this just links the returned
 * authorization code to the current account and sends them back.
 */
const CleverCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const code = searchParams.get("code")

  const [error, setError] = useState(() =>
    getCallbackValidationError(searchParams)
  )
  const hasRun = useRef(false)

  useEffect(() => {
    // Clearing here (rather than inside getCallbackValidationError) keeps
    // that function a pure read, safe for StrictMode's dev-only double
    // invocation of lazy useState initializers — this removeItem call is
    // idempotent, so it doesn't matter if the effect itself also runs twice.
    sessionStorage.removeItem(BROWSER_STORAGE_KEYS.cleverOAuthState)

    // The actual link request must only fire once, though — Clever's
    // authorization code is single-use.
    if (hasRun.current || error || !code) return
    hasRun.current = true

    const linkAccount = async () => {
      try {
        await api.auth.socialAuth.link({
          data: {
            provider: SocialAuthProvider.CLEVER,
            authorizationCode: code,
            redirectUri: getCleverRedirectUri(),
          },
        })
        navigate(routes.accountDetails)
      } catch (err) {
        setError(
          err instanceof APIError && err.errors.message
            ? err.errors.message
            : "We couldn't link your Clever account. Please try again."
        )
      }
    }

    linkAccount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <PageContainer className="h-screen">
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
          <Typography variant="title3">{error}</Typography>
          <Button color="black" onClick={() => navigate(routes.accountDetails)}>
            Back to settings
          </Button>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="h-screen">
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <CircularProgress size="lg" color="black" />
        <Typography variant="largeBody">Linking your Clever account</Typography>
      </div>
    </PageContainer>
  )
}

export default CleverCallback
